import React, { Fragment, useEffect, useLayoutEffect, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Message from 'supportAssistant/components/Chat/Message'
import MessagesLoadingSkeleton from './MessagesLoadingSkeleton'
import BodyText from 'LuxKit/Typography/BodyText'
import moment from 'moment/moment'
import Loading from 'components/Common/Loading/LoadingAnimation'
import Group from 'components/utils/Group'

const WAITING_MESSAGE_DELAY = 8000

const StyledDateBodyText = styled(BodyText)<{ $compact?: boolean }>`
 margin-bottom: ${props => rem(props.$compact ? 8 : 24)};
 margin-top: ${rem(24)};
`

const StyledTimeBodyText = styled(BodyText)<{ $compact?: boolean }>`
  margin-left: ${rem(34)};
  margin-bottom: ${props => rem(props.$compact ? 1 : 8)};
`

const MessageContent = styled.div`
  padding: ${rem(8)};
  border-radius: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  color: ${props => props.theme.palette.neutral.default.one};
  width: max-content;
`

const MessageLoadingWrapper = styled(MessageContent)`
  margin-top: ${rem(24)};
`

interface Props {
  messages: Array<App.ChatMessage>
  loadingMessages?: boolean
  isTyping: boolean
  isAgentTyping: boolean
  onMessagesRendered?: () => void
  compact?: boolean
}

function MessagesListContainer(props: Props) {
  const { messages, loadingMessages, isTyping, isAgentTyping, onMessagesRendered, compact } = props

  const [isTypingGtWait, setIsTypingGtWait] = useState(false)

  // Epoch date 1970, so first message will always be different and therefore display date/time
  let previousDate = moment(0).format('D MMMM YYYY')

  // if isTyping has been true for longer than 5 seconds show text
  useEffect(() => {
    if (isTyping) {
      const timeout = setTimeout(() => {
        setIsTypingGtWait(true)
      }, WAITING_MESSAGE_DELAY)
      return () => clearTimeout(timeout)
    } else {
      setIsTypingGtWait(false)
    }
  }, [isTyping])

  // notify parent when messages have been rendered to the DOM
  useLayoutEffect(() => {
    if (!loadingMessages && messages.length > 0 && onMessagesRendered) {
      onMessagesRendered()
    }
  }, [loadingMessages, messages.length, onMessagesRendered])

  return (
    <Group direction="vertical" gap={compact ? 8 : 16} fullWidth>
      {loadingMessages && <MessagesLoadingSkeleton />}
      {!loadingMessages && <>
        {messages?.map((message, index) => {
          const messageDate = moment(message.createdAt).format('D MMMM YYYY')

          if (messageDate !== previousDate) {
            previousDate = messageDate

            return (
              <Fragment key={`${index}`}>
                <Group direction="vertical" gap={compact ? 4 : 16} fullWidth>
                  <StyledDateBodyText variant="small" align="center" $compact={compact}>
                    {previousDate}
                  </StyledDateBodyText>
                  {index === 0 && <StyledTimeBodyText variant="small" $compact={compact}>{moment(message.createdAt).format('HH:mma')}</StyledTimeBodyText>}
                </Group>
                <Message
                  message={message}
                  key={`${message.createdAt}-${index}`}
                />
              </Fragment>
            )
          }

          return (
            <Message
              message={message}
              key={`${message.createdAt}-${index}`}
            />
          )
        })}
      </>}
      {isTypingGtWait &&
        <Message
          message={{
            sender: 'chatbot',
            text: 'We’re searching for a response to your question, thank you for your patience',
          }}
        />}
      {(isTyping || isAgentTyping) && <MessageLoadingWrapper><Loading size={6} /></MessageLoadingWrapper>}
    </Group>
  )
}

export default MessagesListContainer
